<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import BetaLayoutPreviewModal from '@/components/beta-dashboard/organisms/BetaLayoutPreviewModal.vue'
import { useSiteStore } from '@/stores/site'

import BetaActiveLayoutCard from '@/components/beta-dashboard/molecules/BetaActiveLayoutCard.vue'
import BetaTemplateCard from '@/components/beta-dashboard/molecules/BetaTemplateCard.vue'
import modal from '@/services/modal'
import notification from '@/services/notification'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import type { BetaTemplate } from '@/types/beta-dashboard'

const betaStore = useBetaDashboardStore()
const siteStore = useSiteStore()
const previewedTemplate = ref<BetaTemplate | null>(null)
const previewApplyError = ref<string | null>(null)
const selectedTemplateId = ref<number | null>(null)

const selectedTemplate = computed(() => (
    betaStore.templates.find((template) => template.id === selectedTemplateId.value) ?? null
))
const activeTemplate = computed(() => (
    betaStore.templates.find((template) => (
        Number(template.id) === Number(betaStore.activeLayout?.templateId)
    )) ?? null
))
const availableTemplates = computed(() => (
    betaStore.templates.filter((template) => (
        Number(template.id) !== Number(betaStore.activeLayout?.templateId)
    ))
))
const canApply = computed(() => Boolean(
    selectedTemplate.value
    && selectedTemplate.value.id !== betaStore.activeLayout?.templateId
    && !betaStore.applyingLayout,
))

const selectTemplate = (template: BetaTemplate) => {
    if (!betaStore.applyingLayout) selectedTemplateId.value = template.id
}

const previewTemplate = (template: BetaTemplate) => {
    if (betaStore.applyingLayout) return
    selectedTemplateId.value = template.id
    previewApplyError.value = null
    previewedTemplate.value = template
}

const demoTemplate = (template: BetaTemplate) => {
    if (!template.url) return

    const url = /^https?:\/\//i.test(template.url)
        ? template.url
        : `https://${template.url}`
    window.open(url, '_blank', 'noopener,noreferrer')
}

const applyTemplate = async (previewToken?: string) => {
    if (!selectedTemplate.value || !canApply.value) return

    const confirmed = await modal.confirmation(
        'Aplicar este layout?',
        'Seus textos, imagens, páginas, links e publicações serão mantidos. A aparência e as variantes visuais das seções serão atualizadas.',
        {
            type: 'main',
            textConfirm: 'Aplicar layout',
            textCancel: 'Cancelar',
        },
    )

    if (!confirmed) return

    try {
        if (previewToken) await betaStore.applyTemplate(selectedTemplate.value, previewToken)
        else await betaStore.applyTemplate(selectedTemplate.value)
        previewedTemplate.value = null
        selectedTemplateId.value = null
        notification.simple('Layout aplicado. Seu conteúdo foi preservado.', 'success')
    } catch (error) {
        if (previewToken) previewApplyError.value = error instanceof Error ? error.message : 'Não foi possível aplicar o layout.'
        notification.simple(
            error instanceof Error ? error.message : 'Não foi possível aplicar o layout.',
            'error',
        )
    }
}

const undoTemplate = async () => {
    if (!betaStore.layoutRollback || betaStore.applyingLayout) return

    const confirmed = await modal.confirmation(
        'Desfazer a última troca?',
        `O layout “${betaStore.layoutRollback.templateName}” será aplicado novamente, mantendo o conteúdo atual.`,
        {
            type: 'info',
            textConfirm: 'Desfazer troca',
            textCancel: 'Cancelar',
        },
    )

    if (!confirmed) return

    try {
        await betaStore.undoTemplate()
        selectedTemplateId.value = null
        notification.simple('Layout anterior restaurado.', 'success')
    } catch (error) {
        notification.simple(
            error instanceof Error ? error.message : 'Não foi possível restaurar o layout.',
            'error',
        )
    }
}

watch(
    () => betaStore.activeLayout?.templateId,
    () => {
        selectedTemplateId.value = null
    },
    { immediate: true },
)

onMounted(async () => {
    if (!betaStore.templates.length || !betaStore.activeLayout) {
        await betaStore.loadLayouts()
    }
})
</script>

<template>
    <div class="beta-layout-selector">
        <div class="beta-layout-selector__heading">
            <div>
                <p>Layout</p>
                <h2>Escolha o visual do seu site</h2>
                <span>Os mesmos modelos do Wizard, agora sem perder o conteúdo que você já criou.</span>
            </div>
            <button
                v-if="betaStore.layoutRollback"
                type="button"
                class="beta-layout-selector__undo"
                :disabled="betaStore.applyingLayout"
                @click="undoTemplate"
            >
                Desfazer última troca
            </button>
        </div>

        <div v-if="betaStore.layoutsError" class="beta-layout-selector__error">
            <div>
                <strong>Os layouts não puderam ser carregados.</strong>
                <span>{{ betaStore.layoutsError }}</span>
            </div>
            <button type="button" @click="betaStore.loadLayouts">Tentar novamente</button>
        </div>

        <template v-if="betaStore.loadingLayouts">
            <div class="beta-layout-selector__active-skeleton" />
            <div class="beta-layout-selector__grid">
                <div v-for="index in 3" :key="index" class="beta-layout-selector__skeleton" />
            </div>
        </template>

        <section v-else-if="betaStore.activeLayout" class="beta-layout-selector__active">
            <div class="beta-layout-selector__section-heading">
                <div>
                    <h3>Visual atual</h3>
                    <p>O layout que seus visitantes veem neste momento.</p>
                </div>
            </div>
            <BetaActiveLayoutCard
                :layout="betaStore.activeLayout"
                :template="activeTemplate"
                :disabled="betaStore.applyingLayout"
                @preview="demoTemplate"
            />
        </section>

        <section v-if="!betaStore.loadingLayouts" class="beta-layout-selector__alternatives">
            <div class="beta-layout-selector__section-heading">
                <div>
                    <h3>Outros layouts</h3>
                    <p>Selecione uma alternativa para comparar antes de aplicar.</p>
                </div>
                <span v-if="availableTemplates.length">
                    {{ availableTemplates.length }} {{ availableTemplates.length === 1 ? 'opção' : 'opções' }}
                </span>
            </div>

            <div v-if="availableTemplates.length" class="beta-layout-selector__grid">
                <BetaTemplateCard
                    v-for="template in availableTemplates"
                    :key="template.id"
                    :template="template"
                    :selected="template.id === selectedTemplateId"
                    :disabled="betaStore.applyingLayout"
                    :preview-available="betaStore.activeLayout?.previewAvailable"
                    @select="selectTemplate"
                    @preview="previewTemplate"
                    @demo="demoTemplate"
                />
            </div>

            <div v-else-if="betaStore.templates.length" class="beta-layout-selector__empty">
                Nenhum outro layout está disponível para troca.
            </div>

            <div v-else-if="!betaStore.layoutsError" class="beta-layout-selector__empty">
                Nenhum layout ativo foi encontrado.
            </div>
        </section>

        <BetaLayoutPreviewModal
            v-if="previewedTemplate"
            :template="previewedTemplate"
            :site-id="Number(siteStore.id)"
            :applying="betaStore.applyingLayout"
            :apply-error="previewApplyError"
            @close="previewedTemplate = null"
            @apply="applyTemplate"
            @reset-error="previewApplyError = null"
        />

        <footer class="beta-layout-selector__footer">
            <p>
                <strong>{{ selectedTemplate?.description || 'Selecione um layout' }}</strong>
                <span>Revise a prévia antes de aplicar a mudança.</span>
            </p>
            <button type="button" :disabled="!canApply" @click="applyTemplate()">
                {{ betaStore.applyingLayout ? 'Aplicando…' : 'Aplicar layout' }}
            </button>
        </footer>
    </div>
</template>

<style scoped>
.beta-layout-selector {
    display: grid;
    gap: 20px;
}

.beta-layout-selector__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid #edf1f6;
}

.beta-layout-selector__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-layout-selector__heading h2 {
    margin-top: 4px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 850;
    letter-spacing: -0.025em;
}

.beta-layout-selector__heading span {
    display: block;
    margin-top: 6px;
    color: #7d8da3;
    font-size: 11px;
}

.beta-layout-selector__undo {
    flex: none;
    border-radius: var(--beta-button-radius);
    background: #fff2f2;
    padding: 10px 13px;
    color: #b45454;
    font-size: 10px;
    font-weight: 850;
}

.beta-layout-selector__undo:hover:not(:disabled) {
    background: color-mix(in srgb, #b45454 14%, white);
}

.beta-layout-selector__undo:focus-visible {
    outline: 2px solid #b45454;
    outline-offset: 2px;
}

.beta-layout-selector__active,
.beta-layout-selector__alternatives {
    display: grid;
    gap: 12px;
}

.beta-layout-selector__section-heading {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 16px;
}

.beta-layout-selector__section-heading h3 {
    color: #263b56;
    font-size: 14px;
    font-weight: 850;
}

.beta-layout-selector__section-heading p {
    margin-top: 3px;
    color: #8493a7;
    font-size: 12px;
}

.beta-layout-selector__section-heading > span {
    color: #8493a7;
    font-size: 10px;
    font-weight: 750;
}

.beta-layout-selector__grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
}

.beta-layout-selector__skeleton {
    min-height: 230px;
    border-radius: 18px;
    background: linear-gradient(90deg, #f0f3f6 25%, #f8fafc 50%, #f0f3f6 75%);
    background-size: 200% 100%;
    animation: beta-layout-loading 1.4s infinite;
}

.beta-layout-selector__active-skeleton {
    min-height: 230px;
    border-radius: 22px;
    background: linear-gradient(90deg, #eef2f6 25%, #f8fafc 50%, #eef2f6 75%);
    background-size: 200% 100%;
    animation: beta-layout-loading 1.4s infinite;
}

.beta-layout-selector__error,
.beta-layout-selector__empty {
    border: 1px dashed #dce4ef;
    border-radius: 16px;
    padding: 18px;
    color: #71839b;
    font-size: 12px;
}

.beta-layout-selector__error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-style: solid;
    border-color: #f0cccc;
    background: #fff7f7;
    color: #9b4c4c;
}

.beta-layout-selector__error strong,
.beta-layout-selector__error span {
    display: block;
}

.beta-layout-selector__error span {
    margin-top: 3px;
}

.beta-layout-selector__error button {
    font-weight: 850;
}

.beta-layout-selector__footer {
    position: sticky;
    bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    border: 1px solid #e0e7f1;
    border-radius: 17px;
    background: rgba(255, 255, 255, 0.96);
    padding: 13px 14px;
    backdrop-filter: blur(10px);
}

.beta-layout-selector__footer strong,
.beta-layout-selector__footer span {
    display: block;
}

.beta-layout-selector__footer strong {
    color: #1b314f;
    font-size: 12px;
    font-weight: 850;
}

.beta-layout-selector__footer span {
    margin-top: 3px;
    color: #8190a5;
    font-size: 10px;
}

.beta-layout-selector__footer > button {
    min-height: 42px;
    flex: none;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 17px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

.beta-layout-selector__footer > button:disabled,
.beta-layout-selector__undo:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

@keyframes beta-layout-loading {
    to {
        background-position: -200% 0;
    }
}

@media (max-width: 1100px) {
    .beta-layout-selector__grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 640px) {
    .beta-layout-selector__heading,
    .beta-layout-selector__footer {
        align-items: stretch;
        flex-direction: column;
    }

    .beta-layout-selector__grid {
        grid-template-columns: 1fr;
    }

    .beta-layout-selector__footer {
        bottom: 74px;
    }

    .beta-layout-selector__footer > button {
        width: 100%;
    }
}
</style>
